import React from "react";
import { Box } from "@mui/system";
import { Button, CircularProgress } from "@mui/material";
import { Grid, Typography } from "@mui/joy";
import { useTranslation } from "react-i18next";
import AssetNameplate from "../AssetNameplate";

export interface EventFollowUp {
    key: string;
    /** Why this follow-up is offered (one or two sentences). */
    description: React.ReactNode;
    actionLabel: string;
    onAction: () => void;
    /** Busy while the action runs (e.g. creating a declaration before navigating). */
    loading?: boolean;
    testId?: string;
}

interface Props {
    assetId?: string;
    assetLocationId?: string;
    followUps: EventFollowUp[];
    /** Close without taking any follow-up. */
    onClose: () => void;
    closeLabel?: string;
}

/**
 * Post-success step of the event modal: offers the follow-ups the just-created
 * event makes advisable (IoT config update of the touched system, declaration
 * of contamination for a removed asset). Each follow-up navigates away, so the
 * user picks one; the others stay reachable from their own places (system
 * page, event history). With a single follow-up this renders the same compact
 * two-button layout the IoT prompt always had.
 */
const EventFollowUpPrompt = ({ assetId, assetLocationId, followUps, onClose, closeLabel }: Props) => {
    const { t } = useTranslation();
    const single = followUps.length === 1 ? followUps[0] : null;

    return (
        <Box sx={{ display: "flex", flexDirection: "column" }} data-testid="event-followup-prompt">
            {(assetId || assetLocationId) && (
                <Box sx={{ mt: 1, mb: 1, p: 2, border: "1px solid #ccc", borderRadius: "5px" }}>
                    <AssetNameplate
                        assetLocationId={assetLocationId}
                        assetId={assetLocationId ? undefined : assetId}
                        compact
                        disableNavigation
                        showLocation
                    />
                </Box>
            )}

            {single ? (
                <>
                    <Typography level="body-md" sx={{ my: 2 }}>{single.description}</Typography>
                    <Grid container spacing={1} paddingBottom={0}>
                        <Grid xs={6}>
                            <Button fullWidth variant="outlined" onClick={onClose} data-testid="event-followup-close">
                                {closeLabel ?? t("Close")}
                            </Button>
                        </Grid>
                        <Grid xs={6}>
                            <Button
                                fullWidth
                                variant="contained"
                                onClick={single.onAction}
                                disabled={single.loading}
                                startIcon={single.loading ? <CircularProgress size={16} color="inherit" /> : undefined}
                                data-testid={single.testId}
                            >
                                {single.actionLabel}
                            </Button>
                        </Grid>
                    </Grid>
                </>
            ) : (
                <>
                    <Typography level="body-md" sx={{ mt: 2, mb: 1 }}>
                        {t("The event was created. Recommended next steps:")}
                    </Typography>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2 }}>
                        {followUps.map((followUp) => (
                            <Box
                                key={followUp.key}
                                data-testid={followUp.testId}
                                sx={{
                                    p: 2, border: "1px solid #ccc", borderRadius: "5px",
                                    display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap",
                                }}
                            >
                                <Typography level="body-md" sx={{ flex: 1, minWidth: 200 }}>{followUp.description}</Typography>
                                <Button
                                    variant="contained"
                                    onClick={followUp.onAction}
                                    disabled={followUp.loading}
                                    startIcon={followUp.loading ? <CircularProgress size={16} color="inherit" /> : undefined}
                                    sx={{ flexShrink: 0 }}
                                >
                                    {followUp.actionLabel}
                                </Button>
                            </Box>
                        ))}
                    </Box>
                    <Button fullWidth variant="outlined" onClick={onClose} data-testid="event-followup-close">
                        {closeLabel ?? t("Close")}
                    </Button>
                </>
            )}
        </Box>
    );
};

export default EventFollowUpPrompt;
